import { FormEvent, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";

import {
  createCustomerType,
  getInstance,
  getInstanceUsage,
  updateCustomerType,
} from "@/api/catalog";
import { listComponents } from "@/api/components";
import { ComposedPayloadPreview } from "@/components/ComposedPayloadPreview";
import { ConfirmModal } from "@/components/ConfirmModal";
import { Checkbox, Field, Fieldset, Input } from "@/components/form";
import { ErrorBox, MutationError, RefreshFailed, Spinner, isLoadingOrPaused, loadFailure } from "@/components/Spinner";
import { GROUP_LABEL } from "@/lib/catalogComponents";
import {
  CAPABILITY_GROUPS,
  type GatewayComponent,
  type GatewayComponentGroup,
  type GatewayRecipe,
} from "@/types/api";
import { FormActionBar, FormPlaceholder, Notice, Page, PageHeader } from "@/components/ui";
import { useDirtyValues } from "@/hooks/useDirtyValues";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";

// Mirrors the backend regexes (lambda-layers/.../models.py: _GATEWAY_MODEL_RE
// / _GATEWAY_VERSION_RE) — same pattern used by CatalogGatewayEdit.tsx.
const MODEL_PATTERN = "[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?";
const VERSION_PATTERN = "v\\d{1,3}\\.\\d{1,3}\\.\\d{1,3}";

const ARCHITECTURES = ["x86_64", "arm64"] as const;
const RELEASE_CHANNELS = ["production", "beta"] as const;

// Groups the composer offers as checkboxes: exactly those that contribute a
// capability. ``core`` and ``features`` describe the gateway's own plumbing
// and form the locked base. Derived from CAPABILITY_GROUPS and GROUP_LABEL
// rather than respelled — the group list and its wording each live once.
const OPTIONAL_GROUPS: { group: GatewayComponentGroup; title: string }[] =
  CAPABILITY_GROUPS.map((group) => ({ group, title: GROUP_LABEL[group] }));

function refOf(component: GatewayComponent): string {
  return `${component.group}/${component.name}`;
}

function componentLabel(ref: string): string {
  return ref.split("/")[1] ?? ref;
}

/**
 * A component declaring ``depends_on`` is only selectable once one of its
 * dependencies is already selected. Without this guard an operator could
 * check a widget that never activates: ``resolve_widgets_from_dependencies``
 * on the dataplane side ignores it in silence.
 */
function dependencyState(
  component: GatewayComponent,
  selected: Set<string>,
): { enabled: boolean; reason: string | null } {
  const dependsOn = component.body.depends_on as Record<string, string[]> | undefined;
  if (!dependsOn || Object.keys(dependsOn).length === 0) {
    return { enabled: true, reason: null };
  }
  const refs = Object.entries(dependsOn).flatMap(([group, names]) =>
    (names ?? []).map((name) => `${group}/${name}`),
  );
  if (refs.some((ref) => selected.has(ref))) {
    return { enabled: true, reason: null };
  }
  const labels = refs.map((ref) => ref.split("/")[1]).join(", ");
  return { enabled: false, reason: `Requires one of: ${labels}` };
}

/**
 * Tenants whose delivered configuration would change if this save goes
 * through. Aggregated from the type's own identity — the tenants that
 * reference this model at this version — never from the union of the
 * checked components' usages.
 *
 * That distinction is the whole point: a component-derived aggregate
 * returns zero the moment nothing is checked, so unchecking *everything*
 * and saving showed no banner and asked for no confirmation — the single
 * most destructive gesture the editor can produce was also the quietest.
 * Identity-derived, it is exact on the version too, which the per-component
 * crossing (tenant id + model only) could not express.
 *
 * A type that doesn't exist yet is referenced by nobody, so a creation
 * legitimately reports zero without a round-trip.
 */
async function computeImpactTenantCount(
  token: string,
  model: string,
  version: string,
): Promise<number> {
  if (!model || !version) return 0;
  const usage = await getInstanceUsage(token, {
    kind: "customer",
    model,
    version,
  });
  return usage.tenantIds.length;
}

interface Identity {
  model: string;
  version: string;
}

interface Metadata {
  displayName: string;
  description: string;
  osSupported: string;
  architecture: string[];
  releaseChannels: string[];
}

interface Bootstrap {
  psVirtAudioCount: number;
  psVirtAudioSubstreams: number;
}

function emptyMetadata(): Metadata {
  return {
    displayName: "",
    description: "",
    osSupported: "linux",
    architecture: [],
    releaseChannels: [],
  };
}

export function CatalogTypeEditPage() {
  const routeParams = useParams<{ model?: string; version?: string }>();
  const isEdit = Boolean(routeParams.model && routeParams.version);
  const routeModel = routeParams.model ?? null;
  const routeVersion = routeParams.version ?? null;

  const auth = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const token = auth.user?.id_token ?? "";

  const queryKey = ["catalog", "gateway", "customer", routeModel, routeVersion] as const;

  const existing = useQuery({
    queryKey,
    queryFn: () => getInstance(token, { kind: "customer", model: routeModel!, version: routeVersion! }),
    enabled: !!token && isEdit,
  });

  const componentsQuery = useQuery({
    queryKey: ["catalog", "components"],
    queryFn: () => listComponents(token),
    enabled: !!token,
  });

  const [identity, setIdentity] = useState<Identity>({
    model: routeModel ?? "",
    version: routeVersion ?? "",
  });
  const [metadata, setMetadata] = useState<Metadata>(emptyMetadata());
  const [selected, setSelected] = useState<Map<string, Record<string, number>>>(new Map());
  const [bootstrap, setBootstrap] = useState<Bootstrap>({
    psVirtAudioCount: 8,
    psVirtAudioSubstreams: 8,
  });
  // Preserved as-is on save — no field in this composer edits it, and
  // dropping it silently would erase a design note some types carry.
  const [designNote, setDesignNote] = useState<string | null>(null);
  const [primed, setPrimed] = useState(false);
  const [cascadeNotice, setCascadeNotice] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmImpact, setConfirmImpact] = useState<{ tenantCount: number } | null>(null);

  // Dirty = what the form shows differs from what it was opened with (the values of the render where
  // it was primed), and the way out of the page is held while it is. What is compared is what the
  // operator can change, in a shape that says the same thing when it says the same thing: the lists
  // whose order means nothing are sorted (ticking a box off and on again moves it to the end) and the
  // picked components, kept in a Map, are an ordered list of [ref, parameters].
  const { dirty, reset: markSaved } = useDirtyValues(
    {
      identity,
      metadata: {
        ...metadata,
        architecture: [...metadata.architecture].sort(),
        releaseChannels: [...metadata.releaseChannels].sort(),
      },
      selected: Array.from(selected.entries()).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)),
      bootstrap,
      designNote,
    },
    primed,
  );
  const guard = useUnsavedChanges(dirty);

  useEffect(() => {
    setPrimed(false);
  }, [routeModel, routeVersion]);

  useEffect(() => {
    if (primed) return;
    if (!isEdit) {
      setPrimed(true);
      return;
    }
    if (!existing.data) return;
    const recipe = existing.data.recipe;
    if (!recipe) {
      // Legacy entry seeded before the components migration — nothing to
      // prime, handled by the guard rendered below instead of the form.
      setPrimed(true);
      return;
    }
    setIdentity({ model: existing.data.model, version: existing.data.version ?? "" });
    setMetadata({
      displayName: recipe.displayName ?? "",
      description: recipe.description,
      osSupported: recipe.osSupported,
      architecture: recipe.architecture,
      releaseChannels: recipe.releaseChannels,
    });
    setBootstrap({ ...recipe.bootstrap });
    setDesignNote(recipe.designNote);
    const map = new Map<string, Record<string, number>>();
    for (const ref of recipe.components) map.set(ref.ref, { ...ref.capability });
    setSelected(map);
    setPrimed(true);
  }, [existing.data, isEdit, primed]);

  const components = componentsQuery.data?.items ?? [];
  const componentByRef = useMemo(() => {
    const map = new Map<string, GatewayComponent>();
    for (const c of components) map.set(refOf(c), c);
    return map;
  }, [components]);
  const alwaysComponents = useMemo(
    () =>
      components
        .filter((c) => c.inclusion === "always")
        .sort((a, b) => refOf(a).localeCompare(refOf(b))),
    [components],
  );
  // The always-included base is never listed in `selected` (it isn't a
  // choice), but it's always shipped — so from a dependency-satisfaction
  // point of view it must count as selected too. Mirrors the backend:
  // catalog_compose._check_dependencies runs over `chosen`, which is the
  // base PLUS the recipe's picks (see _selected() in catalog_compose.py).
  // Passing only `selected.keys()` to dependencyState would diverge from
  // that the day any component declares a depends_on into core/features —
  // today unreachable (no component does), but the two sides must agree
  // regardless of what's reachable today.
  const alwaysRefs = useMemo(
    () => new Set(alwaysComponents.map(refOf)),
    [alwaysComponents],
  );
  function withBase(refs: Iterable<string>): Set<string> {
    return new Set([...alwaysRefs, ...refs]);
  }
  const optionalByGroup = useMemo(() => {
    const map = new Map<GatewayComponentGroup, GatewayComponent[]>();
    for (const { group } of OPTIONAL_GROUPS) {
      map.set(
        group,
        components
          .filter((c) => c.group === group && c.inclusion === "optional")
          .sort((a, b) => a.name.localeCompare(b.name)),
      );
    }
    return map;
  }, [components]);

  function toggleComponent(component: GatewayComponent) {
    const ref = refOf(component);
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(ref)) {
        next.delete(ref);
        // Cascade: drop anything whose only satisfied dependency was this
        // ref, repeatedly, in case that in turn strands a further widget.
        const removed: { ref: string; reason: string }[] = [];
        let changed = true;
        while (changed) {
          changed = false;
          for (const selRef of Array.from(next.keys())) {
            const selComponent = componentByRef.get(selRef);
            if (!selComponent) continue;
            const state = dependencyState(selComponent, withBase(next.keys()));
            if (!state.enabled) {
              next.delete(selRef);
              removed.push({ ref: selRef, reason: state.reason ?? "" });
              changed = true;
            }
          }
        }
        setCascadeNotice(
          removed.length > 0
            ? removed
                .map(
                  ({ ref: removedRef, reason }) =>
                    `${componentLabel(removedRef)} was also removed: ${reason.replace(
                      /^Requires one of: /,
                      "it depends on ",
                    )}.`,
                )
                .join(" ")
            : null,
        );
      } else {
        const defaults: Record<string, number> = {};
        for (const p of component.params) defaults[p.key] = p.default;
        next.set(ref, defaults);
        setCascadeNotice(null);
      }
      return next;
    });
  }

  function setParamValue(ref: string, key: string, value: number) {
    setSelected((prev) => {
      const next = new Map(prev);
      next.set(ref, { ...(next.get(ref) ?? {}), [key]: value });
      return next;
    });
  }

  function toggleListValue(field: "architecture" | "releaseChannels", value: string) {
    setMetadata((m) => {
      const list = m[field];
      const nextList = list.includes(value)
        ? list.filter((v) => v !== value)
        : [...list, value];
      return { ...m, [field]: nextList };
    });
  }

  const saveMut = useMutation({
    mutationFn: () => {
      const recipe: GatewayRecipe = {
        description: metadata.description,
        osSupported: metadata.osSupported,
        architecture: metadata.architecture,
        releaseChannels: metadata.releaseChannels,
        components: Array.from(selected.entries()).map(([ref, capability]) => ({
          ref,
          capability,
        })),
        bootstrap: { ...bootstrap },
        displayName: metadata.displayName.trim() ? metadata.displayName.trim() : null,
        designNote,
      };
      return isEdit
        ? updateCustomerType(token, identity.model, identity.version, recipe)
        : createCustomerType(token, identity.model, identity.version, recipe);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["catalog", "gateways"] });
      qc.invalidateQueries({ queryKey });
      setConfirmImpact(null);
      markSaved();
      if (isEdit) {
        guard.leave("/catalog/gateways");
      } else {
        // Land on the edit route for the type we just created instead of
        // the list: the composed-payload preview needs a persisted recipe
        // to show anything, and this is the first moment one exists.
        guard.leave(
          `/catalog/gateways/types/${encodeURIComponent(identity.model)}/${encodeURIComponent(identity.version)}/edit`,
          { replace: true },
        );
      }
    },
  });

  const impactMut = useMutation({
    // Only an edit can impact anyone: a type being created is referenced by
    // no tenant yet, so there is nothing to look up.
    mutationFn: () =>
      isEdit
        ? computeImpactTenantCount(token, identity.model, identity.version)
        : Promise.resolve(0),
    onSuccess: (tenantCount) => {
      if (tenantCount > 0) {
        setConfirmImpact({ tenantCount });
      } else {
        saveMut.mutate();
      }
    },
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormError(null);
    // A failure left by an earlier attempt must not sit next to the message of this one, nor resurface
    // inside the confirmation modal, which renders ``saveMut.error`` itself.
    saveMut.reset();
    impactMut.reset();
    if (metadata.architecture.length === 0) {
      setFormError("Select at least one architecture.");
      return;
    }
    if (metadata.releaseChannels.length === 0) {
      setFormError("Select at least one release channel.");
      return;
    }
    impactMut.mutate();
  }

  // The title comes from the address, so loading and failing both keep it.
  const header = (
    <PageHeader
      back={{ to: "/catalog/gateways", label: "Gateway types" }}
      title={isEdit ? `Edit ${identity.model}@${identity.version}` : "New gateway type"}
    />
  );
  if (isEdit && isLoadingOrPaused(existing)) return <FormPlaceholder header={header} width="list" />;
  // Only an error with nothing to show replaces the page (see RefreshFailed):
  // a failed refetch must not hide the form the operator is filling in.
  if (isEdit && existing.isLoadingError) {
    return <FormPlaceholder header={header} width="list" failure={loadFailure(existing)} />;
  }

  if (isEdit && existing.data && !existing.data.recipe) {
    return (
      <Page width="form">
        {header}
        <Notice tone="warning">
          This type predates the component catalog and still carries a raw
          configuration blob instead of a set of components. It can't be
          composed here yet — it needs to be migrated to components first.
        </Notice>
      </Page>
    );
  }

  const busy = componentsQuery.isLoading;
  const saving = saveMut.isPending || impactMut.isPending;

  return (
    <Page>
      {header}

      <RefreshFailed query={existing} className="mb-6" />
      {componentsQuery.isError && <ErrorBox error={componentsQuery.error} />}

      <div className="flex flex-col lg:flex-row gap-6 items-start">
        <form onSubmit={handleSubmit} className="max-w-form flex-1">
          <div className="card space-y-6">
            <div>
              <h2 className="text-sm font-medium text-ink mb-3">Identity</h2>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Model">
                  <Input
                    mono
                    value={identity.model}
                    required
                    disabled={isEdit}
                    pattern={MODEL_PATTERN}
                    title="DNS-safe slug: starts and ends with [a-z0-9], hyphens allowed in the middle (64 characters max)."
                    onChange={(e) => setIdentity((i) => ({ ...i, model: e.target.value }))}
                  />
                </Field>
                <Field label="Version">
                  <Input
                    mono
                    value={identity.version}
                    required
                    disabled={isEdit}
                    placeholder="v1.0.0"
                    pattern={VERSION_PATTERN}
                    title="Semver vX.Y.Z (1 to 3 digits per segment, e.g. v1.0.0)"
                    onChange={(e) => setIdentity((i) => ({ ...i, version: e.target.value }))}
                  />
                </Field>
              </div>
              {isEdit && (
                <p className="mt-1 text-xs text-ink-muted">
                  Model and version cannot be changed — each version is
                  immutable. Create a new version to publish a revision.
                </p>
              )}
            </div>

            <div className="space-y-4">
              <h2 className="text-sm font-medium text-ink">Metadata</h2>
              <Field label="Display name">
                <Input
                  value={metadata.displayName}
                  onChange={(e) => setMetadata((m) => ({ ...m, displayName: e.target.value }))}
                />
              </Field>
              <Field label="Description">
                <Input
                  value={metadata.description}
                  required
                  onChange={(e) => setMetadata((m) => ({ ...m, description: e.target.value }))}
                />
              </Field>
              <Field label="Operating system">
                <Input
                  mono
                  value={metadata.osSupported}
                  required
                  onChange={(e) => setMetadata((m) => ({ ...m, osSupported: e.target.value }))}
                />
              </Field>
              <Fieldset legend="Architecture">
                <div className="mt-1 flex gap-4">
                  {ARCHITECTURES.map((arch) => (
                    <Checkbox
                      key={arch}
                      label={<span className="font-mono">{arch}</span>}
                      checked={metadata.architecture.includes(arch)}
                      onChange={() => toggleListValue("architecture", arch)}
                    />
                  ))}
                </div>
              </Fieldset>
              <Fieldset legend="Release channels">
                <div className="mt-1 flex gap-4">
                  {RELEASE_CHANNELS.map((channel) => (
                    <Checkbox
                      key={channel}
                      label={channel}
                      checked={metadata.releaseChannels.includes(channel)}
                      onChange={() => toggleListValue("releaseChannels", channel)}
                    />
                  ))}
                </div>
              </Fieldset>
            </div>

            {busy && <Spinner label="Loading components…" />}

            {!busy && (
              <>
                <details className="rounded border border-line bg-sunken">
                  <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-ink">
                    Included in every type ({alwaysComponents.length} components)
                  </summary>
                  <ul className="px-3 pb-3 text-sm text-ink-muted">
                    {alwaysComponents.map((c) => (
                      <li key={refOf(c)} className="py-1">
                        <span className="font-mono">{refOf(c)}</span>
                        {typeof c.body.description === "string"
                          ? ` — ${c.body.description}`
                          : null}
                      </li>
                    ))}
                  </ul>
                </details>

                {cascadeNotice && (
                  // Set by a click on a checkbox above: announced without moving focus.
                  <Notice tone="warning" role="status">
                    {cascadeNotice}
                  </Notice>
                )}

                <div className="space-y-5">
                  {OPTIONAL_GROUPS.map(({ group, title: groupTitle }) => {
                    const items = optionalByGroup.get(group) ?? [];
                    if (items.length === 0) return null;
                    return (
                      <div key={group}>
                        <h3 className="text-sm font-medium text-ink mb-2">{groupTitle}</h3>
                        <div className="space-y-2">
                          {items.map((component) => {
                            const ref = refOf(component);
                            const checked = selected.has(ref);
                            const state = dependencyState(component, withBase(selected.keys()));
                            const description =
                              typeof component.body.description === "string"
                                ? component.body.description
                                : null;
                            return (
                              <div key={ref} className="rounded border border-line p-3">
                                <Checkbox
                                  label={
                                    <>
                                      <span className="font-mono">{component.name}</span>
                                      {description ? ` — ${description}` : null}
                                      {!state.enabled && (
                                        <span className="ml-2 text-xs text-warning-fg">
                                          ({state.reason})
                                        </span>
                                      )}
                                    </>
                                  }
                                  checked={checked}
                                  disabled={!state.enabled}
                                  onChange={() => toggleComponent(component)}
                                />
                                {checked && component.params.length > 0 && (
                                  <div className="mt-2 ml-6 flex flex-wrap gap-4">
                                    {component.params.map((p) => (
                                      <Field
                                        key={p.key}
                                        className="w-32"
                                        label={<span className="font-mono text-xs">{p.key}</span>}
                                        help={`${p.minimum}–${p.maximum}`}
                                      >
                                        <Input
                                          type="number"
                                          min={p.minimum}
                                          max={p.maximum}
                                          value={selected.get(ref)?.[p.key] ?? p.default}
                                          onChange={(e) =>
                                            setParamValue(ref, p.key, Number(e.target.value))
                                          }
                                        />
                                      </Field>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            <div>
              <h2 className="text-sm font-medium text-ink mb-3">Bootstrap</h2>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Virtual sound cards">
                  <Input
                    type="number"
                    min={1}
                    max={16}
                    required
                    value={bootstrap.psVirtAudioCount}
                    onChange={(e) =>
                      setBootstrap((b) => ({ ...b, psVirtAudioCount: Number(e.target.value) }))
                    }
                  />
                </Field>
                <Field label="Channels per card">
                  <Input
                    type="number"
                    min={1}
                    max={32}
                    required
                    value={bootstrap.psVirtAudioSubstreams}
                    onChange={(e) =>
                      setBootstrap((b) => ({
                        ...b,
                        psVirtAudioSubstreams: Number(e.target.value),
                      }))
                    }
                  />
                </Field>
              </div>
              <p className="mt-1 text-xs text-ink-muted">
                Virtual sound cards let the gateway expose independent audio
                devices to its host, each carrying this many channels. Most
                types need a single card with a handful of channels — raise
                these only for gateways serving many programs at once.
              </p>
            </div>
          </div>

          {/* While the confirmation modal is up it shows the save's error itself. */}
          <FormActionBar
            dirty={dirty}
            submitLabel={isEdit ? "Save" : "Create type"}
            pending={saving}
            disabled={busy}
            disabledReason="Loading the components…"
            onCancel={() => navigate("/catalog/gateways")}
            error={
              <>
                {formError && (
                  <Notice tone="danger" role="alert">
                    {formError}
                  </Notice>
                )}
                {!confirmImpact && <MutationError mutation={saveMut} title="Could not save the gateway type" />}
                <MutationError mutation={impactMut} title="Could not check who is affected" />
              </>
            }
          />
        </form>

        <ComposedPayloadPreview
          model={isEdit ? identity.model : null}
          version={isEdit ? identity.version : null}
        />
      </div>

      {confirmImpact && (
        <ConfirmModal
          title="Save this gateway type"
          confirmLabel="Save"
          pendingLabel="Saving…"
          pending={saveMut.isPending}
          error={saveMut.error}
          onCancel={() => {
            saveMut.reset();
            setConfirmImpact(null);
          }}
          onConfirm={() => saveMut.mutate()}
        >
          <p>
            This will change the configuration delivered to{" "}
            {confirmImpact.tenantCount} tenant
            {confirmImpact.tenantCount > 1 ? "s" : ""}. Continue?
          </p>
        </ConfirmModal>
      )}
    </Page>
  );
}
